import { useState } from 'react'

import { Alert, Box, Icon } from '@factorialco/deprecated-design-system'
import { LegalEntityForSelectFragment } from 'generated/resources/graphql'
import moment from 'moment'

import { observer } from 'mobx-react'

import { EmployeeForSelectFragmentNoAccess } from 'modules/core/components/EntitySelectWrapper/Entities/Employees/types'
import DayPicker from 'modules/core/components/Inputs/DayPicker'
import TextInput from 'modules/core/components/Inputs/Text'
import TextArea from 'modules/core/components/Inputs/TextArea'
import { LegalEntitySelector } from 'modules/core/components/LegalEntitySelector'
import hasFeature from 'modules/core/lib/hasFeature'
import i18n from 'modules/core/lib/i18n'

import { EmployeeSelector } from 'modules/employees/components/EmployeeSelector'
import type manualIncidenceForm from 'modules/payroll_bundle/pages/IncidencesPage/utils/manualIncidenceForm'

interface Props {
  form: manualIncidenceForm
  isEditing?: boolean
  showLegalEntitySelector?: boolean
}

const ManualIncidenceForm = ({ form, isEditing, showLegalEntitySelector = true }: Props) => {
  const employeeIdField = form.get('employee_id')
  const legalEntityIdField = form.get('legal_entity_id')
  const legalEntityId = legalEntityIdField.value
  const [selectedEmployee, setSelectedEmployee] =
    useState<EmployeeForSelectFragmentNoAccess | null>(null)
  const [selectedLegalEntity, setSelectedLegalEntity] =
    useState<LegalEntityForSelectFragment | null>(null)

  const handleSelection = (employee: EmployeeForSelectFragmentNoAccess | null) => {
    if (employee) {
      setSelectedEmployee(employee)
      employeeIdField.set(employee?.id)
    }
  }

  const handleLegalEntityChange = (legalEntity: LegalEntityForSelectFragment | null) => {
    if (legalEntity) {
      setSelectedLegalEntity(legalEntity)
      legalEntityIdField.set(legalEntity.id + '')
    }
  }

  return (
    <Box gap="s16">
      {showLegalEntitySelector && (
        <LegalEntitySelector
          label={i18n.t('bookkeepers.incidence.manual.form.labels.legal_entity')}
          value={selectedLegalEntity}
          onChange={handleLegalEntityChange}
          width="full"
        />
      )}
      <EmployeeSelector
        label={i18n.t('bookkeepers.incidence.manual.form.labels.employee')}
        onSelect={handleSelection}
        value={selectedEmployee}
        usingAccessIds={false}
        disabled={isEditing}
        legalEntityIds={legalEntityId ? [Number(legalEntityId)] : undefined}
        {...(!showLegalEntitySelector && {
          hideSearchIcon: true,
          emptyLabel: i18n.t('common.select'),
        })}
      />
      <TextInput
        mandatory
        label={i18n.t('bookkeepers.incidence.manual.form.labels.subject')}
        field={form.get('subject')}
      />
      <TextArea
        mandatory
        label={i18n.t('bookkeepers.incidence.manual.form.labels.description')}
        field={form.get('description')}
      />
      <DayPicker
        label={i18n.t('bookkeepers.incidence.manual.form.labels.effective_date')}
        field={form.get('effective_on')}
        minDate={moment().subtract(1, 'day')}
      />
      {!form.get('id').value && hasFeature('factorial_professionals') && (
        <Alert color="warning">
          <Box flexDirection="row" alignItems="center" width="full">
            <Icon.Medium icon="info-circle-line" color="grey800" />
            {i18n.t('bookkeepers.incidence.manual.form.labels.todo_warning')}
          </Box>
        </Alert>
      )}
    </Box>
  )
}

export default observer(ManualIncidenceForm)
